/** @jsxImportSource @emotion/react */
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "../../../components/colors";
import { ExternalLinkButton } from "../../../components/ExternalLinkButton";
import { SpinnerBlock } from "../../common/Spinner";
import { Banner } from "../dashboard-components/Banner";
import { CardHeader } from "../../../components/CardHeader";
import { useEffect, useState } from "react";
import { css } from "@emotion/react";
import { breakpoints } from "../../../components/breakpoints";
import { formatMoneyAmount } from "../../../util";
import * as authSelectors from "../../auth/authSelectors";
import { useSelector } from "react-redux";
import moment from "moment";
import { useGetDashboardTotals } from "../service/dashboard-totals-api";
import { useHandleFetchError } from "../../../error-handler";

const bannerWrapperStyle = css`
  display: grid;
  gap: 16px;
  ${breakpoints.desktop} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  ${breakpoints.mobile} {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
`;

export const DashboardMetricsSection = () => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [currencySeperatedTotals, setCurrencySeperatedTotals] = useState<
    Record<string, number>
  >({});
  const {
    data: totals,
    isLoading: totalsAreLoading,
    error,
  } = useGetDashboardTotals({
    organizationKeyName,
    fromDateTime: moment().startOf("day").toISOString(),
    toDateTime: moment().endOf("day").toISOString(),
    period: "day",
    format: "json",
  });

  useEffect(() => {
    if (!totals || totalsAreLoading) {
      return;
    }

    const currencySeperatedTotals: Record<
      string,
      number
    > = totals.totals.reduce((acc, next) => {
      const existingValue = acc[next.realized_revenue_incl_vat.currency] || 0;
      acc[next.realized_revenue_incl_vat.currency] =
        existingValue + next.realized_revenue_incl_vat.amount;
      return acc;
    }, {} as Record<string, number>);

    setCurrencySeperatedTotals(currencySeperatedTotals);
  }, [totals, totalsAreLoading]);

  useHandleFetchError(error);

  return (
    <div className="card">
      <CardHeader title="Today's performance">
        <ExternalLinkButton
          backgroundColor={colors.transparent}
          hoverColor={colors.ctaLight}
          textColor={colors.ctaMain}
          width="fit-content"
          icon={<FontAwesomeIcon icon={solid("arrow-up-right-from-square")} />}
          href="https://mixpanel.com/login/"
          text="Explore all metrics"
        ></ExternalLinkButton>
      </CardHeader>
      <div className="card-body d-flex flex-column">
        {totalsAreLoading ? (
          <SpinnerBlock />
        ) : (
          <div css={bannerWrapperStyle}>
            {Object.entries(currencySeperatedTotals).map(([key, value]) => {
              return (
                <Banner
                  key={key}
                  header={`Total revenue today incl. VAT from orders paid in ${key}`}
                  icon={<FontAwesomeIcon icon={solid("sack-dollar")} />}
                >
                  {formatMoneyAmount(value, key)}
                </Banner>
              );
            })}
            {totals?.totals.length === 0 && (
              <Banner
                header="Total revenue incl. VAT today"
                icon={<FontAwesomeIcon icon={solid("sack-dollar")} />}
              >
                0
              </Banner>
            )}
            <Banner
              header="Total orders today"
              icon={<FontAwesomeIcon icon={solid("tags")} />}
            >
              {totals?.totals.reduce((acc, next) => acc + next.order_count, 0)}
            </Banner>
          </div>
        )}
      </div>
    </div>
  );
};
